/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { CalendarEvent, ProviderCalendar, ProviderCalEventRecord } from '@repo/common';
import { setEndOfDay } from '@repo/toolslib/src/utils/date.util';
import { encodePairs } from '@repo/toolslib/src/utils/url.util';
import { get } from 'svelte/store';
import { userAuthToken } from '../stores/auth.store';
import { convertEventFormat } from '../utils/event.utils';
import { getPrivateClient } from './common.data';

export const fetchCalendarEvents = async (
  enabledCalendars: ProviderCalendar[],
  start: Date,
  end: Date
): Promise<CalendarEvent[] | void> => {
  // Fetches calendars by proxying through the calendars' endpoint.
  const authToken = get(userAuthToken);
  if (!authToken.idToken || !authToken.refreshToken) {
    return [];
  }
  const client = getPrivateClient(authToken);

  if (!enabledCalendars?.length) {
    return [];
  }

  console.log('[fetchCalendarEvents]: ', start, end);

  // Encode pairs into a query string format
  const calendarIdPairs = enabledCalendars.map((calendar) => {
    return [calendar.providerId, calendar.id];
  });

  const queryString = encodePairs(calendarIdPairs);

  const response = await client.get('/providers/events', {
    params: {
      pairs: queryString, // Add the encoded pairs as a query parameter
      start: start.toISOString(),
      end: setEndOfDay(end).toISOString() // Go until last second of day.
    }
  });
  const data = response.data as ProviderCalEventRecord;
  const result: CalendarEvent[] = [];

  for (const [_, providerRecord] of Object.entries(data)) {
    for (const calEvent of providerRecord.c) {
      const calendarId = calEvent.id;
      result.push(
        ...calEvent.c.map((event) => convertEventFormat(event, providerRecord.id, calendarId))
      );
    }
  }
  return result;
};
